import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { trpcClient } from "@/trpc";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "@/hooks/use-organization";
import { OrgNotificationPreferences } from "@/components/settings/OrgNotificationPreferences";
import { TwoFactorSettingsCard } from "@/features/mfa/TwoFactorSettingsCard";
import { OrganizationTermsCard } from "@/components/settings/organization-terms-card";

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
});

function Settings() {
  const { user } = Route.useRouteContext();
  const { selectedOrg, selectedOrgBusinessUnit } = useOrganization();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: user?.first_name || "",
      lastName: user?.last_name || "",
    },
  });

  const [buttonState, setButtonState] = useState<"idle" | "saving" | "success">(
    "idle",
  );

  const updateUserMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string }) =>
      trpcClient.user.updateOrgUser.mutate(data),
    onSuccess: async () => {
      setButtonState("success");
      setTimeout(() => setButtonState("idle"), 2000);
      await queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: () => {
      setButtonState("idle");
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setButtonState("saving");
    updateUserMutation.mutate({
      firstName: values.firstName,
      lastName: values.lastName,
    });
  }

  return (
    <div className="p-2 pt-4 md:pt-8 md:p-8 flex justify-center">
      <div className="space-y-6 max-w-3xl w-full">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground">
            Manage your profile and how you're notified about activity in{" "}
            {selectedOrgBusinessUnit.name}.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Update your personal information.</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={buttonState !== "idle"}
                    className={buttonState === "success" ? "bg-green-600" : ""}
                  >
                    {buttonState === "saving" && "Saving..."}
                    {buttonState === "success" && "Saved!"}
                    {buttonState === "idle" && "Save changes"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        <OrganizationTermsCard organizationId={selectedOrg.id} />

        <OrgNotificationPreferences
          organizationBusinessUnitId={selectedOrgBusinessUnit.id}
          businessUnitName={selectedOrgBusinessUnit.name}
        />

        <TwoFactorSettingsCard />
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/settings/",
)({
  component: Settings,
});
